import { useCallback } from 'react';
import { getFirestore, doc, updateDoc, arrayUnion } from 'firebase/firestore';

/**
 * Custom hook for executing LLM-detected actions
 * @param {Object} config - Configuration object
 * @param {React.RefObject} config.actionPanelRef - Ref to ActionPanel component
 * @param {Function} config.onQueryResult - Callback to update query result
 * @param {Function} config.onSaveQuery - Callback to save query
 * @param {Function} config.onAddToLibrary - Callback to add to library
 * @param {Function} config.onScheduleQuery - Callback to schedule query (placeholder)
 * @param {Function} config.onShareUrl - Callback to share URL (placeholder)
 */
export const useQueryActions = ({
  actionPanelRef,
  onQueryResult,
  onSaveQuery,
  onAddToLibrary,
  onScheduleQuery,
  onShareUrl
}) => {

  /**
   * Log action execution to Firestore
   */
  const logActionExecution = useCallback(async (documentId, action, status, result = null, error = null) => {
    if (!documentId) return;

    try {
      const db = getFirestore();
      const docRef = doc(db, 'LLM Queries', documentId);
      
      const executionLog = {
        type: action.type,
        status: status, // 'success' | 'error' | 'skipped'
        timestamp: new Date(),
        duration: result?.duration || 0,
        params: action.params || {},
        error: error?.message || null,
        result: result || null
      };

      // Add to the executed actions array
      await updateDoc(docRef, {
        'actionLog.executed': arrayUnion(executionLog)
      });

      console.log(`Action ${action.type} logged with status: ${status}`);
    } catch (logError) {
      console.error('Failed to log action execution:', logError);
      // Don't throw - logging failure shouldn't break action execution
    }
  }, []);

  /**
   * Execute CHANGE_VIEW action
   */
  const executeChangeViewAction = useCallback(async (action, responseData) => {
    const startTime = Date.now();
    
    try {
      console.log('Executing CHANGE_VIEW action:', action);
      
      let { viewMode, chartType } = action.params;
      
      // FIX: Convert generic "chart" view mode to specific chart type
      if (viewMode === 'chart') {
        // Use the specified chartType or default to 'bar'
        viewMode = chartType || 'bar';
        console.log(`Converting viewMode from "chart" to "${viewMode}"`);
      }
      
      // Validate the requested view mode
      const validViewModes = ['table', 'bar', 'line', 'pie'];
      if (!validViewModes.includes(viewMode)) {
        throw new Error(`Invalid view mode: ${viewMode}`);
      }

      // Validate chart type if specified
      const validChartTypes = ['bar', 'line', 'pie'];
      if (chartType && !validChartTypes.includes(chartType)) {
        throw new Error(`Invalid chart type: ${chartType}`);
      }

      // Build the card settings to apply
      const cardSettingsUpdate = {
        viewMode: viewMode,
        ...(chartType && { chartType: chartType })
      };

      // If switching to a chart view and no specific chart type provided, 
      // use the view mode as chart type (for backward compatibility)
      if (viewMode !== 'table' && !chartType) {
        cardSettingsUpdate.chartType = viewMode;
      }

      const duration = Date.now() - startTime;

      // Log successful execution
      if (responseData?.documentId) {
        await logActionExecution(
          responseData.documentId, 
          action, 
          'success', 
          { 
            duration,
            appliedSettings: cardSettingsUpdate,
            previousState: null, // We don't have access to previous state here
            newState: cardSettingsUpdate
          }
        );
      }

      console.log(`CHANGE_VIEW action completed in ${duration}ms:`, cardSettingsUpdate);

      return {
        success: true,
        cardSettings: cardSettingsUpdate,
        message: `View changed to ${viewMode}${chartType ? ` (${chartType})` : ''}`
      };

    } catch (error) {
      const duration = Date.now() - startTime;
      
      // Log failed execution
      if (responseData?.documentId) {
        await logActionExecution(
          responseData.documentId, 
          action, 
          'error', 
          { duration },
          error
        );
      }

      console.error('CHANGE_VIEW action failed:', error);
      
      return {
        success: false,
        error: error.message,
        message: `Failed to change view: ${error.message}`
      };
    }
  }, [logActionExecution]);

/**
 * Execute TRANSPOSE_DATA action
 */
const executeTransposeAction = useCallback(async (action, responseData) => {
  const startTime = Date.now();
  
  try {
    console.log('Executing TRANSPOSE_DATA action');
    
    const duration = Date.now() - startTime;
    
    // Log successful execution
    if (responseData?.documentId) {
      await logActionExecution(
        responseData.documentId, 
        action, 
        'success', 
        { 
          duration,
          message: 'Transpose transformation applied'
        }
      );
    }
    
    return {
      success: true,
      cardSettings: {
        transformationType: 'transpose'
      },
      message: 'Data will be transposed'
    };
    
  } catch (error) {
    const duration = Date.now() - startTime;
    
    if (responseData?.documentId) {
      await logActionExecution(
        responseData.documentId, 
        action, 
        'error', 
        { duration },
        error
      );
    }
    
    return {
      success: false,
      error: error.message,
      message: `Failed to transpose: ${error.message}`
    };
  }
}, [logActionExecution]);

/**
 * Execute PIVOT_DATA action
 */
const executePivotAction = useCallback(async (action, responseData) => {
  const startTime = Date.now();
  
  try {
    console.log('Executing PIVOT_DATA action:', action);
    
    // For pivot, we'll use smart defaults if no config provided
    let pivotConfig = action.params?.config;
    
    if (!pivotConfig && responseData?.result?.length > 0) {
      // Smart defaults: find date/string columns for rows, first other column for columns, numeric for values
      const firstRow = responseData.result[0];
      const columns = Object.keys(firstRow);
      const schema = responseData.schema;
      
      // Find suitable columns
      const dateColumns = columns.filter(col => {
        const field = schema?.fields?.find(f => f.name === col);
        return field && (field.type?.includes('DATE') || field.type?.includes('TIMESTAMP'));
      });
      
      const stringColumns = columns.filter(col => {
        const field = schema?.fields?.find(f => f.name === col);
        return field && field.type === 'STRING';
      });
      
      const numericColumns = columns.filter(col => {
        const field = schema?.fields?.find(f => f.name === col);
        return field && ['INTEGER', 'FLOAT', 'NUMERIC'].includes(field.type?.toUpperCase() || '');
      });
      
      // Build smart defaults
      const rowField = dateColumns[0] || stringColumns[0] || columns[0];
      const columnField = stringColumns.find(c => c !== rowField) || 
                         columns.find(c => c !== rowField) || columns[1];
      const valueField = numericColumns.find(c => c !== rowField && c !== columnField) || 
                        columns.find(c => c !== rowField && c !== columnField) || columns[2];
      
      pivotConfig = {
        rowField,
        columnField,
        valueField,
        aggregationType: 'sum'
      };
      
      console.log('Using smart pivot defaults:', pivotConfig);
    }
    
    const duration = Date.now() - startTime;
    
    // Log successful execution
    if (responseData?.documentId) {
      await logActionExecution(
        responseData.documentId, 
        action, 
        'success', 
        { 
          duration,
          pivotConfig,
          message: 'Pivot transformation applied'
        }
      );
    }
    
    return {
      success: true,
      cardSettings: {
        transformationType: 'pivot',
        pivotConfig: pivotConfig
      },
      message: `Data will be pivoted${pivotConfig ? ' with smart defaults' : ''}`
    };
    
  } catch (error) {
    const duration = Date.now() - startTime;
    
    if (responseData?.documentId) {
      await logActionExecution(
        responseData.documentId, 
        action, 
        'error', 
        { duration },
        error
      );
    }
    
    return {
      success: false,
      error: error.message,
      message: `Failed to pivot: ${error.message}`
    };
  }
}, [logActionExecution]);

/**
 * Execute TOGGLE_NUMBER_FORMAT action
 */
const executeToggleNumberFormatAction = useCallback(async (action, responseData) => {
  const startTime = Date.now();
  
  try {
    console.log('Executing TOGGLE_NUMBER_FORMAT action:', action);
    
    const { formatted } = action.params;
    
    // Validate the parameter
    if (typeof formatted !== 'boolean') {
      throw new Error(`Invalid formatted parameter: ${formatted}. Expected boolean.`);
    }
    
    const duration = Date.now() - startTime;
    
    // Log successful execution
    if (responseData?.documentId) {
      await logActionExecution(
        responseData.documentId, 
        action, 
        'success', 
        { 
          duration,
          message: `Number formatting ${formatted ? 'enabled' : 'disabled'}`
        }
      );
    }
    
    return {
      success: true,
      cardSettings: {
        isNumberFormatted: formatted
      },
      message: formatted ? 'Numbers will be formatted as K/M/B' : 'Full numbers will be displayed'
    };
    
  } catch (error) {
    const duration = Date.now() - startTime;
    
    if (responseData?.documentId) {
      await logActionExecution(
        responseData.documentId, 
        action, 
        'error', 
        { duration },
        error
      );
    }
    
    return {
      success: false,
      error: error.message,
      message: `Failed to toggle number format: ${error.message}`
    };
  }
}, [logActionExecution]);

/**
 * Execute SAVE_QUERY action (placeholder)
 */
const executeSaveQueryAction = useCallback(async (action, responseData) => {
    console.log('SAVE_QUERY action detected - triggering save modal');
    
    try {
      // For now, just trigger the existing save functionality
      if (onSaveQuery) {
        onSaveQuery();
        
        // Log successful execution
        if (responseData?.documentId) {
          await logActionExecution(
            responseData.documentId, 
            action, 
            'success', 
            { message: 'Save modal opened' }
          );
        }
        
        return {
          success: true,
          message: 'Save query dialog opened'
        };
      } else {
        throw new Error('Save query handler not available');
      }
    } catch (error) {
      // Log failed execution
      if (responseData?.documentId) {
        await logActionExecution(
          responseData.documentId, 
          action, 
          'error', 
          null,
          error
        );
      }
      
      return {
        success: false,
        error: error.message,
        message: `Failed to save query: ${error.message}`
      };
    }
  }, [onSaveQuery, logActionExecution]);

  /**
   * Execute ADD_TO_DASHBOARD action (placeholder)
   */
  const executeAddToDashboardAction = useCallback(async (action, responseData) => {
    console.log('ADD_TO_DASHBOARD action detected:', action);
    
    try {
      // For now, this is a placeholder - we'd need to implement dashboard selection logic
      // This could involve checking if the dashboard exists, then adding the query to it
      
      const dashboardName = action.params?.dashboardName;
      if (!dashboardName) {
        throw new Error('Dashboard name not specified');
      }

      // Log as skipped for now since we're not fully implementing this yet
      if (responseData?.documentId) {
        await logActionExecution(
          responseData.documentId, 
          action, 
          'skipped', 
          { message: 'ADD_TO_DASHBOARD not fully implemented yet' }
        );
      }
      
      return {
        success: false,
        message: `ADD_TO_DASHBOARD action detected but not yet implemented for dashboard: ${dashboardName}`
      };
    } catch (error) {
      // Log failed execution
      if (responseData?.documentId) {
        await logActionExecution(
          responseData.documentId, 
          action, 
          'error', 
          null,
          error
        );
      }
      
      return {
        success: false,
        error: error.message,
        message: `Failed to add to dashboard: ${error.message}`
      };
    }
  }, [logActionExecution]);

  /**
   * Main function to execute detected actions
   */
  const executeActions = useCallback(async (detectedActions, responseData = null) => {
    console.log('Executing detected actions:', detectedActions);
    
    if (!Array.isArray(detectedActions) || detectedActions.length === 0) {
      return { success: true, results: [] };
    }

    const results = [];
    let mergedCardSettings = {};

    // Execute each action
    for (const action of detectedActions) {
      let result;

      switch (action.type) {
        case 'CHANGE_VIEW':
          result = await executeChangeViewAction(action, responseData);
          if (result.success && result.cardSettings) {
            mergedCardSettings = { ...mergedCardSettings, ...result.cardSettings };
          }
          break;

        case 'SAVE_QUERY':
          result = await executeSaveQueryAction(action, responseData);
          break;

        case 'ADD_TO_DASHBOARD':
          result = await executeAddToDashboardAction(action, responseData);
          break;

        case 'TRANSPOSE_DATA':
          result = await executeTransposeAction(action, responseData);
          if (result.success && result.cardSettings) {
            mergedCardSettings = { ...mergedCardSettings, ...result.cardSettings };
          }
          break;

        case 'PIVOT_DATA':
          result = await executePivotAction(action, responseData);
          if (result.success && result.cardSettings) {
            mergedCardSettings = { ...mergedCardSettings, ...result.cardSettings };
          }
          break;

        case 'TOGGLE_NUMBER_FORMAT':
          result = await executeToggleNumberFormatAction(action, responseData);
          if (result.success && result.cardSettings) {
            mergedCardSettings = { ...mergedCardSettings, ...result.cardSettings };
          }
          break;

        case 'SCHEDULE_QUERY':
        case 'SHARE_URL':
            // Placeholder for future implementation
            console.log(`${action.type} action detected but not yet implemented`);
          result = {
            success: false,
            message: `${action.type} action not yet implemented`
          };
          
          // Log as skipped
          if (responseData?.documentId) {
            await logActionExecution(
              responseData.documentId, 
              action, 
              'skipped', 
              { message: `${action.type} not implemented yet` }
            );
          }
          break;

        default:
          console.warn(`Unknown action type: ${action.type}`);
          result = {
            success: false,
            error: `Unknown action type: ${action.type}`,
            message: `Action type ${action.type} is not supported`
          };
          
          // Log as error
          if (responseData?.documentId) {
            await logActionExecution(
              responseData.documentId, 
              action, 
              'error', 
              null,
              new Error(`Unknown action type: ${action.type}`)
            );
          }
          break;
      }

      results.push({ action: action.type, ...result });
    }

    // Prepare the return object
    const returnValue = {
      success: results.some(r => r.success),
      results: results,
      cardSettings: Object.keys(mergedCardSettings).length > 0 ? mergedCardSettings : null
    };

    console.log('Action execution completed:', returnValue);
    return returnValue;

  }, [executeChangeViewAction, executeSaveQueryAction, executeAddToDashboardAction, executeTransposeAction, executePivotAction, executeToggleNumberFormatAction, logActionExecution]);

  return {
    executeActions
  };
};